import React, { FC } from 'react';

import { Accept } from 'react-dropzone';

import { WithAlloySuspense } from '@adsk/alloy-react-suspense';

import { useAttachmentsContext } from './context/AttachmentsContext';
import AttachmentsContainer from './layout/AttachmentsContainer/AttachmentsContainer';
import { AttachmentsDropzoneProps } from './layout/AttachmentsDropzone/AttachmentsDropzone';
import ConnectedAttachmentsDropzone from './layout/AttachmentsDropzone/ConnectedAttachmentsDropzone';
import ConnectedAttachmentsHeader from './layout/AttachmentsHeader/ConnectedAttachmentsHeader';
import { AttachmentsListProps } from './layout/AttachmentsList/AttachmentsList';
import ConnectedAttachmentsList from './layout/AttachmentsList/ConnectedAttachmentsList';
import { Attachment } from './types';

export type ConnectedAttachmentsProps = {
  renderHeader?: () => React.ReactNode;
  renderDropzone?: () => React.ReactNode;
  openAttachmentWith?: (attachment: Attachment) => void;
  listHeight?: AttachmentsListProps['height'];
  maxFileSize?: number;
  acceptFileTypes?: Accept;
  getCustomItemActions?: AttachmentsListProps['getCustomItemActions'];
  variant?: AttachmentsListProps['variant'];
  showVariantToggle?: AttachmentsListProps['showVariantToggle'];
  customDropzoneTitle?: string;
  className?: string;
  style?: React.CSSProperties;
};

const ConnectedAttachments: FC<ConnectedAttachmentsProps> = ({
  style,
  className,
  listHeight,
  openAttachmentWith,
  maxFileSize,
  acceptFileTypes,
  getCustomItemActions,
  variant,
  showVariantToggle,
  customDropzoneTitle,
  renderHeader = () => (
    <ConnectedAttachmentsHeader
      maxFileSize={maxFileSize}
      acceptFileTypes={acceptFileTypes}
    />
  ),
  renderDropzone = (props: AttachmentsDropzoneProps) => (
    <ConnectedAttachmentsDropzone {...props} />
  ),
  ...props
}) => {
  const { readOnly, maxNumberOfAttachments } = useAttachmentsContext();
  return (
    <AttachmentsContainer className={className} style={style} {...props}>
      {renderHeader()}
      {!readOnly &&
        renderDropzone({
          maxSize: maxFileSize,
          maxNumberOfAttachments,
          accept: acceptFileTypes,
          customDropzoneTitle,
        })}
      <ConnectedAttachmentsList
        renderEmptyState={readOnly ? undefined : () => null}
        openAttachmentWith={openAttachmentWith}
        height={listHeight}
        getCustomItemActions={getCustomItemActions}
        variant={variant}
        showVariantToggle={showVariantToggle}
      />
    </AttachmentsContainer>
  );
};

ConnectedAttachments.displayName = 'Attachments';

export default WithAlloySuspense(ConnectedAttachments);
